/**
 * Initializes Firebase Authentication and provides functions for password management and user logout.
 *
 * @module
 * @description This module configures Firebase Authentication with the given credentials, and provides functions
 * for sending password reset emails, confirming password resets, and signing out users.
 * 
 * @constant {Object} firebaseConfig - Firebase configuration object with necessary credentials.
 * @constant {FirebaseApp} app - Initialized Firebase application instance.
 * @constant {Auth} auth - Firebase Authentication instance.
 * 
 * @function sendPasswordReset - Sends a password reset email to the specified email address.
 * @param {string} email - The email address to send the password reset email to.
 * @returns {Promise<void>} A promise that resolves when the password reset email is sent.
 * @throws Will throw an error if there is an issue with sending the password reset email.
 * 
 * @function sendPasswordForgot - Confirms the password reset using the provided out-of-band code and new passwords.
 * @param {string} oobCode - The out-of-band code from the password reset email.
 * @param {string} password - The new password to set.
 * @param {string} confirmpassword - Confirmation of the new password.
 * @returns {Promise<void>} A promise that resolves when the password is successfully reset.
 * @throws Will throw an error if there is an issue with confirming the password reset.
 * 
 * @function logout - Signs out the currently authenticated user.
 * @returns {Promise<void>} A promise that resolves when the user is successfully signed out.
 */
import { initializeApp } from 'firebase/app';
import { getAuth, sendPasswordResetEmail, signOut, confirmPasswordReset } from 'firebase/auth';
import {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID
} from '../Constants';

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  measurementId: FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase and Firebase Authentication
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * Sends a password reset email to the specified email address.
 * 
 * @async
 * @function sendPasswordReset
 * @param {string} email - The email address to send the password reset email to.
 * @returns {Promise<void>} A promise that resolves when the password reset email is sent.
 * @throws Will throw an error if there is an issue with sending the password reset email.
 */
const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    alert("Password reset link sent!");
    window.location.href = '/login';
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

/**
 * Confirms the password reset using the provided out-of-band code and new passwords.
 * 
 * @async
 * @function sendPasswordForgot
 * @param {string} oobCode - The out-of-band code from the password reset email.
 * @param {string} password - The new password to set.
 * @param {string} confirmpassword - Confirmation of the new password.
 * @returns {Promise<void>} A promise that resolves when the password is successfully reset.
 * @throws Will throw an error if there is an issue with confirming the password reset.
 */
const sendPasswordForgot = async (oobCode, password, confirmpassword) => {  
  try {
    if (password !== '' && confirmpassword !== ''){
      if (password !== confirmpassword) {
        alert('Confirm Password does not match');
      } else {
        await confirmPasswordReset(auth, oobCode, password);
        alert("Password reset successfully!");
        window.location.href = '/login';
      }
    }
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

/**
 * Signs out the currently authenticated user.
 * 
 * @async
 * @function logout
 * @returns {Promise<void>} A promise that resolves when the user is successfully signed out.
 */
const logout = async () => {
  await signOut(auth);
};

export {auth, sendPasswordReset, logout, sendPasswordForgot }